"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const experiences = [{
  id: "exp-1",
  title: "Vertical Ascents",
  description: "Conquer towering limestone cliffs with expert guides. Feel the raw texture of the earth as you ascend above the canopy, rewarded with panoramic vistas that few ever witness.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/rock_climbing_adventure.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/rock_climbing_adventure.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/rock_climbing_adventure.png-wvc-sizes",
  imageAlt: "Person rock climbing a steep cliff face",
  link: "/DestinationPagesPage"
}, {
  id: "exp-2",
  title: "Azure Depths",
  description: "Plunge into crystal-clear cenotes and hidden coves. Our private water sports excursions offer an intimate connection with marine life, far from the crowded tourist shores.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/water_sports_kayak.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/water_sports_kayak.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/water_sports_kayak.png-wvc-sizes",
  imageAlt: "Kayaking in clear blue waters",
  link: "/DestinationPagesPage"
}, {
  id: "exp-3",
  title: "Canopy Immersion",
  description: "Traverse ancient rainforests on suspended pathways. Breathe in the rich, humid air and listen to the symphony of exotic wildlife in their untouched natural habitat.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/jungle_trek_canopy.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/jungle_trek_canopy.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/jungle_trek_canopy.png-wvc-sizes",
  imageAlt: "Suspension bridge through a dense jungle canopy",
  link: "/DestinationPagesPage"
}, {
  id: "exp-4",
  title: "Subterranean Echoes",
  description: "Navigate labyrinthine cave systems illuminated only by headlamps. Discover geological wonders formed over millennia in absolute silence and profound darkness.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/cave_exploration_dark.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/cave_exploration_dark.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1634877/img/cave_exploration_dark.png-wvc-sizes",
  imageAlt: "Exploring a dark cave with stalactites",
  link: "/DestinationPagesPage"
}];
function ExperienceCard({
  experience,
  index
}: {
  experience: typeof experiences[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={cn("group transition-all duration-1000 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12", index % 2 !== 0 ? "md:mt-32" : "")}>
      <Link to={experience.link} className="block w-full h-full">
        <Card className="bg-foreground border-none rounded-none overflow-hidden h-full shadow-2xl">
          <div className="relative overflow-hidden">
            <AspectRatio ratio={4 / 5}>
              <img src={experience.imageSrc} data-wvc-srcset={experience.imageSrcset} data-wvc-sizes={experience.imageSizes} alt={experience.imageAlt} loading="lazy" className="object-cover w-full h-full transition-transform duration-700 ease-in-out group-hover:scale-105 saturate-[0.85]" />
            </AspectRatio>
            {}
            <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_40px_rgba(0,0,0,0.6)]" />
          </div>
          
          <CardContent className="p-8 md:p-10 relative z-10 bg-foreground">
            <h3 className="font-default font-semibold text-secondary text-2xl md:text-3xl mb-4 tracking-wide">
              {experience.title}
            </h3>
            <p className="font-sans text-background/80 text-[0.9rem] leading-relaxed mb-6">
              {experience.description}
            </p>
            
            <div className="overflow-hidden h-6">
              <div className="flex items-center gap-2 text-primary font-sans text-sm font-medium tracking-wider uppercase transition-transform duration-500 ease-out translate-y-full group-hover:translate-y-0">
                <span>Discover More</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    </div>;
}
export default function ExclusiveExperiences() {
  return <section id="exclusive-experiences" className="py-[160px] bg-background overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          {experiences.map((exp, i) => <ExperienceCard key={exp.id} experience={exp} index={i} />)}
        </div>
      </div>
    </section>;
}